/**
 * useSourcesIndex — Plan 07-04 Task 2.
 *
 * Hook for the /sources route: fetches sources.json, exposes the full record
 * list, filter state (type / archive / search), derived counts per type and
 * per archive, and a memoized filtered list.
 *
 * Why this is a separate hook from `useSources` (content/useSources.ts):
 *   - `useSources` loads records into a Zustand store at App root so the 3D
 *     Sources panel (Phase 4 Plan 04-03 SourcesPanel) can render synchronously
 *     without re-fetching.
 *   - `useSourcesIndex` is scoped to the /sources route: it fetches on mount,
 *     keeps filter state locally (not in a global store), and exposes
 *     index-specific derivations (byType, byArchive, filtered).
 *
 * The /sources route is accessed from outside the 3D experience (deep-link,
 * reachable without WebGL2), so it cannot rely on the in-Canvas store.
 *
 * Source shape: `/manifest/sources.json` — `{ version, sources: [...] }` where
 * each record matches the runtime SourceRecord shape from `store/useSourcesStore.ts`.
 *
 * Filter semantics:
 *   - `type: null` → all types pass.
 *   - `archive: null` → all archives pass. String match is case-insensitive and
 *     runs on `record.archive ?? record.attribution` so Phase 2-authored records
 *     (archive field populated) and Phase 7-ingested records (archive derived
 *     from attribution) both work.
 *   - `search: ''` → no filter. Non-empty string matches case-insensitively
 *     against `title` OR `how_we_know`.
 *
 * No runtime Zod validation — sources.json is validated at build time by
 * `tools/sources-schema.test.mjs` (ajv). Keeping the runtime shape permissive
 * avoids shipping a ~60KB ajv bundle to the browser for already-vetted data.
 */
import { useEffect, useMemo, useState } from 'react';
import type { SourceRecord } from '../store/useSourcesStore';

const SOURCES_URL = '/manifest/sources.json';

export type SourcesFilter = {
  type: string | null;
  archive: string | null;
  search: string;
};

export type SourcesIndex = {
  /** All records loaded from sources.json (empty until fetch resolves). */
  records: SourceRecord[];
  /** Filtered view of records given the current filter state. */
  filtered: SourceRecord[];
  /** True from mount until fetch resolves (success or failure). */
  loading: boolean;
  /** Current filter state. */
  filter: SourcesFilter;
  /** Apply a partial filter patch (merge semantics). */
  setFilter: (patch: Partial<SourcesFilter>) => void;
  /** Count per record type across the full record set. */
  byType: Record<string, number>;
  /** Count per archive across the full record set. */
  byArchive: Record<string, number>;
};

function archiveOf(record: SourceRecord): string {
  // Phase-2 records populate `archive` explicitly. Fall back to attribution
  // for early records where archive is absent.
  const a = (record.archive ?? record.attribution ?? '').toString();
  return a.trim();
}

export function useSourcesIndex(): SourcesIndex {
  const [records, setRecords] = useState<SourceRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filter, setFilterState] = useState<SourcesFilter>({
    type: null,
    archive: null,
    search: '',
  });

  useEffect(() => {
    let cancelled = false;
    fetch(SOURCES_URL)
      .then((r) => {
        if (!r.ok) {
          throw new Error(`Sources fetch failed: ${r.status} ${r.statusText}`);
        }
        return r.json();
      })
      .then((data: SourceRecord[] | { version: string; sources: SourceRecord[] }) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : data?.sources;
        if (!Array.isArray(arr)) {
          throw new Error('Sources payload shape invalid — expected array or {sources: [...]}');
        }
        setRecords(arr);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[useSourcesIndex]', e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byType = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const r of records) {
      m[r.type] = (m[r.type] ?? 0) + 1;
    }
    return m;
  }, [records]);

  const byArchive = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const r of records) {
      const a = archiveOf(r);
      if (!a) continue;
      m[a] = (m[a] ?? 0) + 1;
    }
    return m;
  }, [records]);

  const filtered = useMemo<SourceRecord[]>(() => {
    const s = filter.search.trim().toLowerCase();
    const archiveQ = filter.archive?.toLowerCase() ?? '';
    return records.filter((r) => {
      if (filter.type && r.type !== filter.type) return false;
      if (archiveQ && !archiveOf(r).toLowerCase().includes(archiveQ)) return false;
      if (s) {
        const title = r.title.toLowerCase();
        const hwk = (r.how_we_know ?? '').toLowerCase();
        if (!title.includes(s) && !hwk.includes(s)) return false;
      }
      return true;
    });
  }, [records, filter]);

  const setFilter = (patch: Partial<SourcesFilter>) => {
    setFilterState((prev) => ({ ...prev, ...patch }));
  };

  return { records, filtered, loading, filter, setFilter, byType, byArchive };
}
